import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

// Types
export type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  setTheme: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>('light');
  const [initialized, setInitialized] = useState(false);

  // On mount: check localStorage or system preference
  useEffect(() => {
    try {
      const stored = localStorage.getItem('theme');
      if (stored === 'light' || stored === 'dark') {
        setMode(stored);
      } else {
        setMode('light');
      }
    } finally {
      setInitialized(true);
    }
  }, []);

  // When mode changes, update localStorage (only after initialization to avoid clobbering stored value)
  useEffect(() => {
    if (!initialized) return;
    localStorage.setItem('theme', mode);
  }, [mode, initialized]);

  // Sync theme changes across tabs/windows
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'theme' && (e.newValue === 'light' || e.newValue === 'dark')) {
        setMode(e.newValue as ThemeMode);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const toggleTheme = () => setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  const setTheme = (newMode: ThemeMode) => setMode(newMode);

  const value = useMemo(() => ({ mode, toggleTheme, setTheme }), [mode]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export function useThemeMode() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeMode must be used within ThemeProvider');
  return ctx;
} 